import { useEffect } from 'react';
import { useAppSelector } from '@/app/hooks';
import { applyAppearance } from '@/lib/theme';

/**
 * Applies the theme preference. "system" follows the OS and keeps following it
 * as the OS changes — a fixed snapshot at load would go stale at sunset.
 *
 * It also paints the colours this device has chosen, which apply in both
 * light and dark.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = useAppSelector((state) => state.tenant.theme);
  const appearance = useAppSelector((state) => state.appearance);

  // The chosen colours are a stylesheet rather than inline properties: dark
  // mode is a class on the same element, and only a rule can say "these
  // values, but those ones when dark".
  useEffect(() => {
    applyAppearance(appearance);
  }, [appearance]);

  useEffect(() => {
    const root = document.documentElement;

    const apply = (): void => {
      const prefersDark =
        theme === 'dark' ||
        (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
      root.classList.toggle('dark', prefersDark);
      root.style.colorScheme = prefersDark ? 'dark' : 'light';
    };

    apply();

    if (theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [theme]);

  return <>{children}</>;
}
